import {
  KeyRound,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Shield,
  Users,
  UserX,
} from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type User, useUsers } from "@/hooks/use-users";
import { useSession } from "@/lib/auth-client";
import { getUserStatus, ROLE_DISPLAY_NAMES, type USGRole } from "@/lib/rbac";
import { ChangeRoleDialog } from "./ChangeRoleDialog";
import { CreateUserDialog } from "./CreateUserDialog";
import { DeactivateUserDialog } from "./DeactivateUserDialog";
import { EditUserDialog } from "./EditUserDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; user: User }
  | { type: "resetPassword"; user: User }
  | { type: "changeRole"; user: User }
  | { type: "deactivate"; user: User }
  | { type: "reactivate"; user: User };

function StatusBadge({ user }: { user: User }) {
  const status = getUserStatus(user);
  if (status === "active") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
        Active
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-500/10 px-2 py-0.5 text-xs font-medium text-yellow-600 dark:text-yellow-400">
        Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      Inactive
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const label = ROLE_DISPLAY_NAMES[role as USGRole] ?? role;
  const isOps = role === "ops";
  return (
    <Badge variant={isOps ? "default" : "secondary"} className="text-xs">
      {label}
    </Badge>
  );
}

export function UsersTable() {
  const { data: session } = useSession();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  const {
    data: users = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useUsers({ search: debouncedSearch });

  function closeDialog() {
    setDialog({ type: "none" });
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load users"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search by email..."
            className="flex-1 max-w-sm"
          />
          <span className="text-sm text-muted-foreground">{users.length} users</span>
        </div>
        <Button onClick={() => setDialog({ type: "create" })}>
          <Plus className="mr-2 h-4 w-4" />
          New User
        </Button>
      </div>

      <DataTable
        headers={[
          { label: "Name" },
          { label: "Email" },
          { label: "Role" },
          { label: "Department", className: "hidden lg:table-cell" },
          { label: "Status" },
          { label: "Last Login", className: "hidden md:table-cell" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState icon={Users} title="No users found" description="Try a different search." />
        }
      >
        {users.map((user) => {
          const status = getUserStatus(user);
          return (
            <tr
              key={user.id}
              className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
            >
              <td className="px-4 py-3 font-medium">{user.name}</td>
              <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
              <td className="px-4 py-3">
                <RoleBadge role={user.role} />
              </td>
              <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground text-sm">
                {user.department ?? <span className="text-muted-foreground/50">—</span>}
              </td>
              <td className="px-4 py-3">
                <StatusBadge user={user} />
              </td>
              <td className="hidden md:table-cell px-4 py-3 text-muted-foreground text-sm">
                {user.lastLoginAt ? (
                  new Date(user.lastLoginAt).toLocaleDateString()
                ) : (
                  <span className="text-muted-foreground/50">Never</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" disabled={user.id === session?.user?.id}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setDialog({ type: "edit", user })}>
                      <Pencil className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDialog({ type: "changeRole", user })}>
                      <Shield className="h-4 w-4" />
                      Change Role
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDialog({ type: "resetPassword", user })}>
                      <KeyRound className="h-4 w-4" />
                      Reset Password
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {status === "inactive" ? (
                      <DropdownMenuItem onClick={() => setDialog({ type: "reactivate", user })}>
                        <RefreshCw className="h-4 w-4" />
                        Reactivate
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDialog({ type: "deactivate", user })}
                      >
                        <UserX className="h-4 w-4" />
                        Deactivate
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          );
        })}
      </DataTable>

      <CreateUserDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
      />

      {dialog.type === "edit" && (
        <EditUserDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "resetPassword" && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "changeRole" && (
        <ChangeRoleDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {(dialog.type === "deactivate" || dialog.type === "reactivate") && (
        <DeactivateUserDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
          mode={dialog.type}
        />
      )}
    </div>
  );
}
